'use client';

import { useEffect, useState } from 'react';
import { Button } from 'antd';
import { AnimatePresence, motion } from 'framer-motion';
import Link from 'next/link';
import Image from 'next/image';
import { useLogoutMutation } from '@/graphql/auth';
import { HiOutlineBriefcase, HiOutlineDocumentText, HiOutlineFolder, HiOutlinePlusCircle, HiOutlineSearch, HiOutlineUserCircle } from 'react-icons/hi';
import { RiProjectorLine } from 'react-icons/ri';
import { useRouter } from 'next/navigation';
import { BsBoxArrowRight, BsCash, BsChatDots } from 'react-icons/bs';
import ProfileDropdown from '@/components/molecules/drop-down';
import { toast } from 'react-toastify';
import { client } from '@/graphql/lib/apollo';
import { useUser } from '@/services/contexts/UserContext';
import NotificationDropdown from '@/components/atoms/NotificationDropdown';
import { useTranslations } from 'next-intl';
import LanguageSwitcher from '@/components/atoms/LanguageSwitcher';
import { useUnreadMessagesCount } from '@/hooks/useUnreadMessagesCount';
import UnreadBadge from '@/components/atoms/UnreadBadge';

export function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [locale, setLocale] = useState<string>('ar');
  const router = useRouter();
  const { user } = useUser()
  const t = useTranslations("navbar");
  const [logout] = useLogoutMutation();
  const [showNavbar, setShowNavbar] = useState(false);
  const unreadCount = useUnreadMessagesCount();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setShowNavbar(true);
      } else {
        setShowNavbar(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLogout = async () => {
    try {
      const { data } = await logout();
      if (data?.logout.success) {
        await client.clearStore();
        toast.success(data.logout.message || "تم تسجيل الخروج");
        router.push("/sign-in");
      }
    } catch (err) {
      console.error(err);
      toast.error("فشل تسجيل الخروج");
    }
  };

  const toggleLanguage = () => {
    const newLocale = locale === 'ar' ? 'en' : 'ar';
    setLocale(newLocale);
    document.documentElement.dir = newLocale === 'ar' ? 'rtl' : 'ltr';

    router.push(`/${newLocale}${window.location.pathname.replace(/^\/(ar|en)/, '')}`);
  };

  useEffect(() => {
    const currentLocale = window.location.pathname.split('/')[1] || 'ar';
    setLocale(currentLocale);
  }, []);

  return (
    <nav className={` ${showNavbar ? 'fixed' : ''} !w-full top-0 z-50 bg-[var(--color-dark-navy)] border-b border-[var(--color-beige-light)/20] shadow-lg`}>
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsMenuOpen(false)}
            className="fixed inset-0 bg-black/30 z-40 backdrop-blur-sm"
          />
        )}
      </AnimatePresence>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link href="/" className="flex-shrink-0">
            <Image
              src="/imgs/logo-bg.png"
              width={120}
              height={80}
              className="h-auto w-24 hover:scale-105 transition-transform"
              alt="logo"
            />
          </Link>

          <div className="hidden md:flex items-center space-x-8">
            <div className="flex items-center gap-8">
              {
                user && user?.role === "AGENCY" && (
                  <Link
                    href="/profile/add-project"
                    className="flex items-center gap-1 text-white hover:text-[var(--color-gold-bright)] transition-colors duration-300 font-medium text-lg relative group"
                  >
                    <HiOutlinePlusCircle className="text-xl" />
                    {t("addProject")}
                    <span className="absolute bottom-0 left-0 w-0 h-[2px] bg-[var(--color-gold-bright)] transition-all duration-300 group-hover:w-full"></span>
                  </Link>
                )
              }
              {
                user && user?.role === "AGENCY" && (
                  <Link
                    href="/profile/my-projects"
                    className="flex items-center gap-1 text-white hover:text-[var(--color-gold-bright)] transition-colors duration-300 font-medium text-lg relative group"
                  >
                    <HiOutlineFolder className="text-xl" />
                    {t("myProjects")}
                    <span className="absolute bottom-0 left-0 w-0 h-[2px] bg-[var(--color-gold-bright)] transition-all duration-300 group-hover:w-full"></span>
                  </Link>
                )
              }
              {
                user && user?.role === "FREELANCER" && (
                  <Link
                    href="/profile/proposals"
                    className="flex items-center gap-1 text-white hover:text-[var(--color-gold-bright)] transition-colors duration-300 font-medium text-lg relative group"
                  >
                    <HiOutlineDocumentText className="text-xl" />
                    {t("myOffers")}
                    <span className="absolute bottom-0 left-0 w-0 h-[2px] bg-[var(--color-gold-bright)] transition-all duration-300 group-hover:w-full"></span>
                  </Link>
                )
              }
              {
                user && user?.role === "FREELANCER" && (
                  <Link
                    href="/portfolio"
                    className="flex items-center gap-1 text-white hover:text-[var(--color-gold-bright)] transition-colors duration-300 font-medium text-lg relative group"
                  >
                    <HiOutlineBriefcase className="text-xl" />
                    {t("myWork")}
                    <span className="absolute bottom-0 left-0 w-0 h-[2px] bg-[var(--color-gold-bright)] transition-all duration-300 group-hover:w-full"></span>
                  </Link>
                )
              }
              {(!user || user.role === "AGENCY") && user?.role !== "FREELANCER" && (
                <Link
                  href="/freelancers"
                  className="flex items-center gap-1 text-white hover:text-[var(--color-gold-bright)] transition-colors duration-300 font-medium text-lg relative group"
                >
                  <HiOutlineSearch className="text-xl" />
                  {t("findConsultants")}
                  <span className="absolute bottom-0 left-0 w-0 h-[2px] bg-[var(--color-gold-bright)] transition-all duration-300 group-hover:w-full"></span>
                </Link>
              )}
              <Link
                href="/projects"
                className="flex items-center gap-1 text-white hover:text-[var(--color-gold-bright)] transition-colors duration-300 font-medium text-lg relative group"
              >
                <RiProjectorLine className="text-xl" />
                {t("browseProjects")}
                <span className="absolute bottom-0 left-0 w-0 h-[2px] bg-[var(--color-gold-bright)] transition-all duration-300 group-hover:w-full"></span>
              </Link>
            </div>

            <div className="ml-8 flex items-center gap-4">

              <LanguageSwitcher locale={locale} toggleLanguage={toggleLanguage} />

              <div className="flex gap-4">
                {
                  user ? (
                    <>
                      <NotificationDropdown />
                      <ProfileDropdown unreadCount={unreadCount} user={user} />
                    </>
                  ) : (
                    <>
                      <Link href='/sign-up'>
                        <Button
                          block
                          type="primary"
                          className="!bg-[var(--color-olive-dark)] hover:!bg-[var(--color-gold-bright)] !text-lg !py-4 !rounded-xl"
                        >
                          {t("createAccount")}
                        </Button>
                      </Link>
                      <Link href='/sign-in'>
                        <Button
                          block
                          type="primary"
                          className="!bg-[var(--color-gold-bright)] hover:!bg-[var(--color-olive-dark)] !text-lg !py-4 !rounded-xl"
                        >
                          {t("login")}
                        </Button>
                      </Link>
                    </>
                  )}
              </div>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-4">
            {/* Notification Dropdown for mobile */}
            <LanguageSwitcher locale={locale} toggleLanguage={toggleLanguage} />
            {user && <NotificationDropdown />}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded-lg hover:bg-[var(--color-gray-light)] transition-colors"
            >
              <div className="w-8 h-1 bg-[var(--color-gold-bright)] mb-1 rounded"></div>
              <div className="w-8 h-1 bg-[var(--color-gold-bright)] mb-1 rounded"></div>
              <div className="w-8 h-1 bg-[var(--color-gold-bright)] rounded"></div>
            </button>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: isMenuOpen ? 0 : '100%' }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="fixed top-0 right-0 h-full w-[85%] bg-[var(--color-dark-navy)] backdrop-blur-lg shadow-2xl z-50 p-6"
      >
        <div className="flex flex-col h-full">
          <div className="flex justify-end mb-8">
            <button
              onClick={() => setIsMenuOpen(false)}
              className="text-3xl text-[var(--color-white)] hover:text-[var(--color-gold-bright)] transition-colors p-2"
            >
              &times;
            </button>
          </div>

          <div className="flex flex-col gap-2 flex-grow">
            {
              user && user?.role === "AGENCY" && (
                <Link
                  onClick={() => setIsMenuOpen(false)}
                  href="/profile/add-project"
                  className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <HiOutlinePlusCircle className="text-xl text-[var(--color-gold-bright)]" />
                  <span className="text-xl text-[var(--color-white)] font-medium">
                    {t("addProject")}
                  </span>
                </Link>
              )
            }
            {
              user && user?.role === "AGENCY" && (
                <Link
                  onClick={() => setIsMenuOpen(false)}
                  href="/profile/my-projects"
                  className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <HiOutlineFolder className="text-xl text-[var(--color-gold-bright)]" />
                  <span className="text-xl text-[var(--color-white)] font-medium">
                    {t("myProjects")}
                  </span>
                </Link>
              )
            }
            {
              user && user?.role === "FREELANCER" && (
                <Link
                  href="/profile/proposals"
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <HiOutlineDocumentText className="text-xl text-[var(--color-gold-bright)]" />
                  <span className="text-xl text-[var(--color-white)] font-medium">
                    {t("myOffers")}
                  </span>
                </Link>
              )
            }
            {
              user && user?.role === "FREELANCER" && (
                <Link
                  href="/portfolio"
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <HiOutlineBriefcase className="text-xl text-[var(--color-gold-bright)]" />
                  <span className="text-xl text-[var(--color-white)] font-medium">
                    {t("myWork")}
                  </span>
                </Link>
              )
            }

            {
              user && (
                <Link
                  href="/profile/payments"
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <BsCash className="text-xl text-[var(--color-gold-bright)]" />
                  <span className="text-xl text-[var(--color-white)] font-medium">
                    {t("payments")}
                  </span>
                </Link>
              )
            }

            {(!user || user.role === "AGENCY") && user?.role !== "FREELANCER" && (
              <Link
                href="/freelancers"
                onClick={() => setIsMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-[var(--color-olive-light)] transition-all"
              >
                <HiOutlineSearch className="text-xl text-[var(--color-gold-bright)]" />
                <span className="text-xl text-[var(--color-white)] font-medium">
                  {t("findConsultants")}
                </span>
              </Link>
            )
            }

            <Link
              href="/projects"
              onClick={() => setIsMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-[var(--color-olive-light)] transition-all"
            >
              <RiProjectorLine className="text-xl text-[var(--color-gold-bright)]" />
              <span className="text-xl text-[var(--color-white)] font-medium">
                {t("browseProjects")}
              </span>
            </Link>
          </div>

          <div className="mt-auto pt-8 border-t border-[var(--color-gray-light)]">
            {user ? (
              <div className="flex items-center justify-between">
                <Link
                  href="/profile/chats"
                  onClick={() => setIsMenuOpen(false)}
                  className="relative p-2 text-[var(--color-gold-bright)] hover:text-[var(--color-olive-dark)] duration-500 transition-colors"
                  title="الرسائل"
                >
                  <BsChatDots className="text-2xl" />
                  <UnreadBadge count={unreadCount} />
                </Link>
                <Link
                  href="/profile"
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center gap-2"
                >
                  {user?.avatarUrl ? (
                    <Image
                      quality={80}
                      unoptimized={true}
                      width={40}
                      height={40}
                      src={`${user?.avatarUrl}`}
                      alt="Profile Avatar"
                      className="w-10 h-10 rounded-full object-cover border-2 border-[var(--color-gold-bright)]"
                    />
                  ) : (
                    <div className="bg-[var(--color-olive-light)] p-2 rounded-full">
                      <HiOutlineUserCircle className="text-2xl text-[var(--color-gold-bright)]" />
                    </div>
                  )}
                </Link>

                <button
                  onClick={handleLogout}
                  className="flex flex-col items-center justify-center text-[var(--color-olive-dark)] hover:text-[var(--color-olive-darker)] transition-colors"
                  aria-label="Logout"
                >
                  <div className="bg-[var(--color-olive-light)] hover:bg-[var(--color-olive-dark)] p-3 rounded-full transition-colors">
                    <BsBoxArrowRight className="text-xl text-[var(--color-gold-bright)]" />
                  </div>
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                <Link href='/sign-up'>
                  <Button
                    block
                    type="primary"
                    onClick={() => setIsMenuOpen(false)}
                    className="!bg-[var(--color-olive-dark)] hover:!bg-[var(--color-olive-darker)] !text-white !py-3 !rounded-lg !text-base !font-medium"
                  >
                    {t("createAccount")}
                  </Button>
                </Link>
                <Link href='/sign-in'>
                  <Button
                    block
                    type="primary"
                    onClick={() => setIsMenuOpen(false)}
                    className="!bg-transparent !border !border-[var(--color-white)] !text-[var(--color-white)] hover:!bg-[var(--color-olive-light)] !py-3 !rounded-lg !text-base !font-medium"
                  >
                    {t("login")}
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </nav>
  );
};